import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuditLogCategory, CreateAuditLogDto } from '@cdw-ae/common-data';
import { Observable } from 'rxjs';
import { ApiUrlBuilderService } from '../api-url-builder.service';

/**
 * Parameters for creating an audit log entry
 */
export interface CreateAuditLogParams {
  /**
   * Database user ID from claims (core.dbUserId)
   * This is the foreign key reference to the users table
   */
  userId: number;
  category: AuditLogCategory;
  operationType: string;
  action: string;
  description: string;
  additionalData?: Record<string, unknown>;
  jobId?: string | null;
}

/**
 * Service for building and creating audit logs from the frontend
 *
 * Usage:
 * ```typescript
 * this.auditLogBuilder.createAuditLog({
 *   userId: userData.claims.core.dbUserId, // Database user ID
 *   category: AuditLogCategory.AUTHENTICATION,
 *   operationType: 'Login',
 *   action: 'User Logged In',
 *   description: 'User successfully logged in'
 * }).subscribe(() => console.log('Audit log created'));
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AuditLogBuilderService {
  constructor(
    private httpClient: HttpClient,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  /**
   * Create an audit log entry
   * This is the primary method - use this for all audit log creation
   *
   * `keepalive` (EDUS-2657): set for writes that race a hard navigation
   * immediately after the call (e.g. the logout redirect) so the request
   * survives the document unloading. Requires the app's `HttpClient` to be on
   * the fetch backend (`provideHttpClient(..., withFetch())`) — on XHR,
   * Angular ignores it with a console warning.
   */
  public createAuditLog(
    params: CreateAuditLogParams,
    options?: { keepalive?: boolean }
  ): Observable<{ success: boolean }> {
    const {
      userId,
      category,
      operationType,
      action,
      description,
      additionalData = {},
      jobId = null,
    } = params;

    const actionData = {
      userId,
      action,
      category,
      operationType,
      description,
      ...additionalData,
    };

    const auditLog: CreateAuditLogDto = {
      userId,
      category,
      operationType,
      action,
      actionData: JSON.stringify(actionData),
      jobId,
    };

    const url = this.urlBuilder.backendApi('audit-log', 'create');
    return this.httpClient.post<{ success: boolean }>(url, auditLog, {
      keepalive: options?.keepalive,
    });
  }
}
